import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Wrap routes that require a signed-in user. If unauthenticated, opens the
 * global AuthDialog (from AuthContext) and shows a friendly gate instead of
 * a redirect — this preserves the intended URL so the user lands on the
 * gated route after signing in.
 */
export default function RequireAuth({ children }) {
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (auth && !auth.loading && !auth.user) {
      auth.setAuthMode?.("login");
      auth.setAuthOpen?.(true);
    }
  }, [auth?.loading, auth?.user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!auth || auth.loading) {
    return (
      <main className="dyntra-container py-24 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" />
      </main>
    );
  }

  if (!auth.user) {
    return (
      <main className="dyntra-container py-16">
        <div className="max-w-lg mx-auto rounded-2xl border border-gray-100 bg-white shadow-sm p-8 text-center">
          <div className="h-12 w-12 mx-auto rounded-xl bg-[#F3EFFC] flex items-center justify-center">
            <Lock className="h-5 w-5 text-[#4B2AAD]" />
          </div>
          <h1 className="mt-4 font-display text-2xl font-bold text-gray-900">Sign in required</h1>
          <p className="mt-2 text-sm text-gray-600">
            The AI Consultant workspace is only available to signed-in dental professionals. Sign in or create a free account to continue.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => { auth.setAuthMode?.("login"); auth.setAuthOpen?.(true); }}
              className="dyntra-btn-primary px-5 py-2.5 text-sm"
              data-testid="require-auth-login"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => { auth.setAuthMode?.("register"); auth.setAuthOpen?.(true); }}
              className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              data-testid="require-auth-register"
            >
              Create account
            </button>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            Or <Link to="/" className="text-[#4B2AAD] font-semibold hover:underline">return to the homepage</Link>.
          </p>
          <p className="sr-only">You were trying to visit {location.pathname}.</p>
        </div>
      </main>
    );
  }

  return children;
}
